<script setup lang="ts">
import { computed } from 'vue'
const props = defineProps({
  section: {
    type: String,
    required: true,
    validator: (value: string) =>
      ['overview', 'jobs', 'salaries', 'reviews', 'dress-code', 'drug-test-policy', 'hiring-process'].includes(value)
  }
})
const showDate = computed(() => {
  return props.section !== 'overview'
})
const formattedDate = computed(() => {
  if (!showDate.value) return ''
  const today = new Date()
  switch (props.section) {
    case 'jobs':
      return formatDate(today)
    case 'salaries': {
      const anchor = new Date(2023, 1, 1)
      const daysSinceAnchor = Math.floor(
        (today.getTime() - anchor.getTime()) / (24 * 60 * 60 * 1000)
      )
      const twoWeeksSinceAnchor = Math.floor(daysSinceAnchor / 14)
      const lastUpdateDate = new Date(anchor)
      lastUpdateDate.setDate(anchor.getDate() + twoWeeksSinceAnchor * 14)
      return formatDate(lastUpdateDate)
    }
    case 'dress-code': {
      const anchor = new Date(2023, 1, 1)
      const monthsSinceAnchor
        = (today.getFullYear() - anchor.getFullYear()) * 12
        + today.getMonth()
        - anchor.getMonth()
      const quartersSinceAnchor = Math.floor(monthsSinceAnchor / 3)
      const lastUpdateDate = new Date(anchor)
      lastUpdateDate.setMonth(anchor.getMonth() + quartersSinceAnchor * 3)
      return formatDate(lastUpdateDate)
    }
    case 'drug-test-policy': {
      const anchor = new Date(2023, 1, 1)
      const monthsSinceAnchor
        = (today.getFullYear() - anchor.getFullYear()) * 12
        + today.getMonth()
        - anchor.getMonth()
      const quartersSinceAnchor = Math.floor(monthsSinceAnchor / 3)
      const lastUpdateDate = new Date(anchor)
      lastUpdateDate.setMonth(anchor.getMonth() + quartersSinceAnchor * 3)
      return formatDate(lastUpdateDate)
    }
    case 'hiring-process': {
      const anchor = new Date(2023, 1, 1)
      const yearsSinceAnchor = today.getFullYear() - anchor.getFullYear()
      const lastUpdateDate = new Date(anchor)
      lastUpdateDate.setFullYear(anchor.getFullYear() + yearsSinceAnchor)
      // If the calculated date is in the future, use the previous year
      if (lastUpdateDate > today) {
        lastUpdateDate.setFullYear(lastUpdateDate.getFullYear() - 1)
      }
      return formatDate(lastUpdateDate)
    }
    case 'reviews': {
      const anchor = new Date(2023, 1, 1)
      const daysSinceAnchor = Math.floor(
        (today.getTime() - anchor.getTime()) / (24 * 60 * 60 * 1000)
      )
      const weeksSinceAnchor = Math.floor(daysSinceAnchor / 7)
      const lastUpdateDate = new Date(anchor)
      lastUpdateDate.setDate(anchor.getDate() + weeksSinceAnchor * 7)
      return formatDate(lastUpdateDate)
    }
    default:
      return ''
  }
})
const sectionName = computed(() => {
  switch (props.section) {
    case 'jobs':
      return 'Job posting data '
    case 'salaries':
      return 'Salary data '
    case 'reviews':
      return 'Review information '
    case 'dress-code':
      return 'Dress code information '
    case 'drug-test-policy':
      return 'Drug test information '
    case 'hiring-process':
      return 'Hiring process information '
    default:
      return ''
  }
})
function formatDate(date: Date): string {
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ]
  const month = months[date.getMonth()]
  const day = date.getDate()
  const year = date.getFullYear()
  return `${month} ${day}, ${year}`
}
</script>
<template>
  <p class="update-note text-center py-4 text-gray-500">
    {{ sectionName }} last updated {{ formattedDate }}.
  </p>
</template>